import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { AssessmentState, TeacherProfile, User } from "./types";
import { apiUrl } from "@/config/api";

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const profile = ref<TeacherProfile | null>(null);
  const availableProfiles = ref<TeacherProfile[]>([]);

  // Assessment states cache - stores assessment states by profile ID and assessment ID
  const assessmentStatesCache = ref<
    Record<string, Record<string, AssessmentState>>
  >({});

  // Assessment list cache - stores list of assessment states by profile ID
  const assessmentListCache = ref<
    Record<string, Array<{ assessment_id: string }>>
  >({});

  // Getters
  const isAuthenticated = computed(() => !!user.value);
  const isProfileSelected = computed(() => !!profile.value);
  const currentProfileName = computed(() => profile.value?.name || "");

  // Assessment state getters
  const getCurrentProfileAssessmentStates = computed(() => {
    if (!profile.value) return {};
    return assessmentStatesCache.value[profile.value.id] || {};
  });

  const getCurrentProfileAssessmentList = computed(() => {
    if (!profile.value) return [];
    return assessmentListCache.value[profile.value.id] || [];
  });

  // Actions
  const login = (userData: User, profiles: TeacherProfile[]) => {
    user.value = userData;
    availableProfiles.value = profiles;
    profile.value = null; // Clear any existing profile on new login

    // Store in localStorage
    localStorage.setItem("user", JSON.stringify(userData));

    return { user: userData, profiles };
  };

  const clearUser = () => {
    user.value = null;
    profile.value = null;
    availableProfiles.value = [];
    localStorage.removeItem("user");
    localStorage.removeItem("profile");
  };

  const setProfile = (newProfile: TeacherProfile | null) => {
    const wasProfileUpdate =
      profile.value && newProfile && profile.value.id === newProfile.id;
    profile.value = newProfile;

    if (newProfile) {
      localStorage.setItem("profile", JSON.stringify(newProfile));

      // Sync to backend if this was an update to existing profile
      if (wasProfileUpdate) {
        void syncProfileStateToBackend();
      } else {
        console.log(
          "Skipped update to backend as this wasn't a profile update",
        );
      }
    } else {
      localStorage.removeItem("profile");
    }
  };

  // Get assessment state from cache or API
  const getAssessmentState = async (
    assessmentId: string,
    forceRefresh = false,
  ): Promise<AssessmentState | null> => {
    if (!profile.value) {
      console.warn(
        `⚠️ Cannot fetch assessment state for ${assessmentId}. No profile selected!`,
      );
      return null;
    }

    const profileId = profile.value.id;

    // Return cached data immediately if available and not forcing refresh
    if (
      !forceRefresh &&
      assessmentStatesCache.value[profileId]?.[assessmentId]
    ) {
      return assessmentStatesCache.value[profileId][assessmentId];
    }

    try {
      const response = await fetch(
        apiUrl(`/profile/${profileId}/assessment_states/${assessmentId}`),
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (response.status === 404) {
        // Assessment state doesn't exist yet, return null but don't cache the null
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        assessmentState: AssessmentState;
      };

      // Cache the result
      if (!assessmentStatesCache.value[profileId]) {
        assessmentStatesCache.value[profileId] = {};
      }
      assessmentStatesCache.value[profileId][assessmentId] =
        data.assessmentState;

      return data.assessmentState;
    } catch (error) {
      console.error(
        `❌ Failed to fetch assessment state for ${assessmentId}:`,
        error,
      );
      return null;
    }
  };

  const updateAssessmentState = async (
    assessmentId: string,
    update: Partial<AssessmentState>,
  ): Promise<boolean> => {
    if (!profile.value) {
      console.warn(
        `⚠️ Could not update assessment state ${Object.keys(update).join(
          ", ",
        )} for ${assessmentId}. No profile selected!`,
      );
      return false;
    }

    try {
      // First get the current state
      const currentState = await getAssessmentState(assessmentId);

      // Merge with the update
      const updatedState = {
        ...(currentState || {}),
        ...update,
      };

      const response = await fetch(
        apiUrl(
          `/profile/${profile.value.id}/assessment_states/${assessmentId}`,
        ),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(updatedState),
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Update cache with the merged state
      const profileId = profile.value.id;
      if (!assessmentStatesCache.value[profileId]) {
        assessmentStatesCache.value[profileId] = {};
      }
      assessmentStatesCache.value[profileId][assessmentId] =
        updatedState as AssessmentState;

      console.log(
        `💾 Assessment state ${Object.keys(update).join(
          ", ",
        )} updated for ${assessmentId}`,
      );
      return true;
    } catch (error) {
      console.error(
        `❌ Failed to update assessment state for ${assessmentId}:`,
        error,
      );
      return false;
    }
  };

  // Load assessment list from cache or API
  const loadAssessmentList = async (
    forceRefresh = false,
  ): Promise<Array<{ assessment_id: string }>> => {
    if (!profile.value) {
      console.warn("⚠️ Cannot load assessment list. No profile selected!");
      return [];
    }

    const profileId = profile.value.id;

    // Return cached data immediately if available and not forcing refresh
    if (!forceRefresh && assessmentListCache.value[profileId]) {
      return assessmentListCache.value[profileId];
    }

    try {
      const response = await fetch(
        apiUrl(`/profile/${profileId}/assessment_states`),
        {
          method: "GET",
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = (await response.json()) as {
        assessmentStates: Array<{ assessment_id: string }>;
      };

      // Cache the result
      assessmentListCache.value[profileId] = data.assessmentStates;

      return data.assessmentStates;
    } catch (error) {
      console.error("❌ Failed to load assessment list:", error);
      return [];
    }
  };

  // Note: clearAssessmentState is not implemented for API-based storage
  // Assessment states are managed at the database level and should be
  // marked as completed rather than deleted for audit purposes
  const clearAssessmentState = (assessmentId: string) => {
    console.warn(
      `⚠️ clearAssessmentState for ${assessmentId} is deprecated. Use completion workflow instead.`,
    );
  };

  // Debounced assessment state updates
  const assessmentStateUpdateTimeouts = new Map<string, number>();
  const ASSESSMENT_STATE_DEBOUNCE_DELAY = 500; // 500ms

  // Debounced version of updateAssessmentState
  const updateAssessmentStateDebounced = (
    assessmentId: string,
    update: Partial<AssessmentState>,
  ) => {
    if (!profile.value) {
      console.warn(
        `⚠️ Cannot update assessment state for ${assessmentId}. No profile selected!`,
      );
      return;
    }

    const profileId = profile.value.id;
    const updateKey = `${profileId}-${assessmentId}`;

    // Update cache immediately for responsive UI
    if (!assessmentStatesCache.value[profileId]) {
      assessmentStatesCache.value[profileId] = {};
    }

    const currentCached =
      assessmentStatesCache.value[profileId][assessmentId] || {};
    assessmentStatesCache.value[profileId][assessmentId] = {
      ...currentCached,
      ...update,
    };

    // Clear existing timeout for this assessment
    const existingTimeout = assessmentStateUpdateTimeouts.get(updateKey);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new debounced timeout
    const newTimeout = window.setTimeout(async () => {
      try {
        await updateAssessmentState(assessmentId, update);
        assessmentStateUpdateTimeouts.delete(updateKey);
      } catch (error) {
        console.error("Failed to update assessment state:", error);
      }
    }, ASSESSMENT_STATE_DEBOUNCE_DELAY);

    assessmentStateUpdateTimeouts.set(updateKey, newTimeout);
  };

  // Debounced backend sync
  let syncTimeout: number | null = null;
  const SYNC_DEBOUNCE_DELAY = 1000; // 1 second

  const syncProfileStateToBackend = () => {
    if (!profile.value) return;

    // Capture current profile reference to avoid null assertion issues
    const currentProfile = profile.value;

    // Clear existing timeout
    if (syncTimeout) {
      clearTimeout(syncTimeout);
    }

    // Debounce the sync
    syncTimeout = window.setTimeout(async () => {
      try {
        const response = await fetch(
          apiUrl(`/profile/${currentProfile.id}/state`),
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              state: currentProfile.state,
              merge: true,
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        console.log("✅ Profile state synced to backend successfully");
      } catch (error) {
        console.error("❌ Failed to sync profile state to backend:", error);
      } finally {
        syncTimeout = null;
      }
    }, SYNC_DEBOUNCE_DELAY);
  };

  const setAvailableProfiles = (profiles: TeacherProfile[]) => {
    availableProfiles.value = profiles;
  };

  const getProfile = async (profileId: number): Promise<TeacherProfile> => {
    try {
      const url = new URL(apiUrl(`/profile/${profileId}`));

      const response = await fetch(url.toString(), {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch profile");
      }

      const data = (await response.json()) as { profile: TeacherProfile };

      return data.profile;
    } catch (error) {
      console.error("Profile fetch error:", error);
      throw error;
    }
  };

  const selectProfile = async (profileId: number): Promise<TeacherProfile> => {
    // Use the new consolidated API with select=true
    const profile = await getProfile(profileId);
    setProfile(profile);
    return profile;
  };

  const createProfile = async (
    name: string,
    profileState: Record<string, unknown> = {},
  ): Promise<TeacherProfile> => {
    try {
      const response = await fetch(apiUrl("/create-profile"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, state: profileState }),
      });

      if (!response.ok) {
        throw new Error("Failed to create profile");
      }

      const data = (await response.json()) as { profile: TeacherProfile };

      // Add the new profile to available profiles
      availableProfiles.value = [...availableProfiles.value, data.profile];

      return data.profile;
    } catch (error) {
      console.error("Profile creation error:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await fetch(apiUrl("/logout"), {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    clearUser();
  };

  const initializeAuth = async () => {
    const storedUser = localStorage.getItem("user");

    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser) as User;
        user.value = userData;

        // Fetch current session info to validate and get available profiles
        try {
          const response = await fetch(apiUrl("/session"), {
            credentials: "include",
          });

          if (response.ok) {
            const sessionData = (await response.json()) as {
              availableProfiles: TeacherProfile[];
              profile?: TeacherProfile;
            };
            setAvailableProfiles(sessionData.availableProfiles);

            // Trust the session data - if backend has a selected profile, use it
            if (sessionData.profile) {
              setProfile(sessionData.profile);
            }
          } else if (response.status === 401) {
            // Session is no longer valid - clear local state and force relogin
            // This is normal on initial page load when not logged in, so handle silently
            clearUser();
          }
        } catch (error) {
          console.error("Failed to fetch session info:", error);
        }

        return {
          user: userData,
          profile: null, // Profile will be set by session validation above
        };
      } catch (error) {
        console.error("Failed to parse stored auth data:", error);
        localStorage.removeItem("user");
        localStorage.removeItem("profile");
      }
    }
    return null;
  };

  return {
    // State - expose ref values directly for proper DevTools display
    user,
    profile,
    availableProfiles,
    assessmentStatesCache,
    assessmentListCache,

    // Getters
    isAuthenticated,
    isProfileSelected,
    currentProfileName,
    currentProfile: profile, // Keep for backward compatibility

    // Assessment state getters
    getCurrentProfileAssessmentStates,
    getCurrentProfileAssessmentList,

    // Actions
    login,
    clearUser,
    setProfile,
    getAssessmentState,
    updateAssessmentState,
    updateAssessmentStateDebounced,
    loadAssessmentList,
    clearAssessmentState,
    setAvailableProfiles,
    getProfile,
    selectProfile,
    createProfile,
    logout,
    initializeAuth,
  };
});
